import React, { useState } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/LoginNew';
import Dashboard from './components/Dashboard';
import InvoiceForm from './components/InvoiceForm';
import InvoicePreview from './components/InvoicePreview';
import UserManagement from './components/UserManagement';

// Main App Content (inside AuthProvider)
const AppContent = () => {
  const { currentUser, userProfile, loading, logout } = useAuth();
  const [currentView, setCurrentView] = useState('dashboard');
  const [previewData, setPreviewData] = useState(null);

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8B0000 0%, #5a0000 100%)' }}>
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 mx-auto text-white mb-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!currentUser || !userProfile) {
    return <Login />;
  }

  const handlePreview = (invoiceData) => {
    setPreviewData(invoiceData);
    setCurrentView('preview');
  };

  const handleSaveInvoice = (invoiceData) => {
    setPreviewData(null);
    setCurrentView('dashboard');
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Check if user can create invoices
  const canCreateInvoice = userProfile.role === 'admin' || userProfile.role === 'staff';
  const canManageUsers = userProfile.role === 'admin';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200">
      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <h1 className="text-2xl font-bold">
                <span className="text-red-800">REMINGTON</span>
                <span className="text-gray-400">|</span>
                <span className="text-gray-800">LLP</span>
              </h1>
              <span className="ml-4 text-sm text-gray-500 border-l border-gray-300 pl-4">
                Invoice System
              </span>
            </div>

            {/* Navigation Links */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setCurrentView('dashboard')}
                className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                  currentView === 'dashboard' 
                    ? 'bg-red-100 text-red-800' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Dashboard
              </button>
              
              {canCreateInvoice && (
                <button
                  onClick={() => setCurrentView('new-invoice')}
                  className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                    currentView === 'new-invoice' || currentView === 'preview'
                      ? 'bg-red-100 text-red-800' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  New Invoice
                </button>
              )}

              {canManageUsers && (
                <button
                  onClick={() => setCurrentView('users')}
                  className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                    currentView === 'users'
                      ? 'bg-red-100 text-red-800' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Users
                </button>
              )}

              {/* User Menu */}
              <div className="flex items-center gap-3 border-l border-gray-200 pl-4 ml-2">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-800">{userProfile.name}</p>
                  <p className="text-xs text-gray-500 capitalize">{userProfile.role}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-500 hover:text-red-800 transition-colors"
                  title="Logout"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {currentView === 'dashboard' && (
          <Dashboard onNewInvoice={() => setCurrentView('new-invoice')} />
        )}
        
        {currentView === 'new-invoice' && canCreateInvoice && (
          <InvoiceForm 
            onPreview={handlePreview}
            onSave={handleSaveInvoice}
          />
        )}
        
        {currentView === 'preview' && previewData && (
          <InvoicePreview 
            invoiceData={previewData}
            onBack={() => setCurrentView('new-invoice')}
            onSave={handleSaveInvoice}
          />
        )}

        {currentView === 'users' && canManageUsers && (
          <UserManagement />
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              © 2026 Remington Advocates LLP. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>finance.remingtonllp.com</span>
              <span>•</span>
              <span>Logged in as {userProfile.email}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Main App with AuthProvider wrapper
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
